import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import client from '../api/client';
import { 
    Calculator, Check, ChevronRight, Package, 
    Tag, Info, Zap, Shield, Sparkles 
} from 'lucide-react';

const PublicCalculator = () => {
    const [selectedItems, setSelectedItems] = useState({
        services: [],
        tariffId: null
    });
    const [globalQuantity, setGlobalQuantity] = useState(1);

    const { data: publicData, isLoading } = useQuery({
        queryKey: ['public-calculator'],
        queryFn: async () => (await client.get('/services-tariffs/public')).data
    });

    const toggleService = (id) => {
        if (!publicData) return;
        
        setSelectedItems(prev => {
            const isSelected = prev.services.includes(id);
            if (isSelected) {
                // remove
                return { ...prev, services: prev.services.filter(sid => sid !== id) };
            } else {
                // add with dependencies
                let toAdd = new Set([id]);
                let currentItem = id;
                
                while(true) {
                    const svc = publicData.services.find(s => s.id === currentItem);
                    if (svc && svc.depends_on && !toAdd.has(svc.depends_on) && !prev.services.includes(svc.depends_on)) {
                        toAdd.add(svc.depends_on);
                        currentItem = svc.depends_on;
                    } else {
                        break;
                    }
                }
                
                return { ...prev, services: [...prev.services, ...Array.from(toAdd)] };
            }
        });
    };

    const selectTariff = (id) => {
        setSelectedItems(prev => ({
            ...prev,
            tariffId: prev.tariffId === id ? null : id
        }));
    };

    const calculateTotal = () => {
        let total = 0;
        if (selectedItems.tariffId) {
            const tariff = publicData?.tariffs.find(t => t.id === selectedItems.tariffId);
            total += tariff?.price || 0;
        }
        selectedItems.services.forEach(sid => {
            const svc = publicData?.services.find(s => s.id === sid);
            if (svc) {
                if (svc.unit === 'шт') {
                    total += (svc.price || 0) * globalQuantity;
                } else {
                    total += svc.price || 0;
                }
            }
        });
        return total;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const { services, tariffs } = publicData || { services: [], tariffs: [] };

    return (
        <div className="min-h-screen bg-black text-white font-sans selection:bg-blue-500/30">
            {/* Header */}
            <header className="relative py-20 px-4 overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-6xl h-full opacity-20">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-blue-600/20 to-transparent blur-[120px]" />
                </div>
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600/10 border border-blue-500/20 text-blue-400 text-[10px] font-black uppercase tracking-widest mb-6">
                        <Sparkles size={14} /> Service Calculator
                    </div>
                    <h1 className="text-5xl md:text-7xl font-black tracking-tighter uppercase mb-6 leading-none">
                        Рассчитайте стоимость <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-400">вашего успеха</span>
                    </h1>
                    <p className="text-neutral-400 text-lg max-w-2xl mx-auto font-medium">
                        Выберите подходящий тариф или соберите индивидуальный набор услуг для вашего бизнеса
                    </p>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 pb-40">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
                    
                    {/* Tariffs section */}
                    <div className="lg:col-span-2 space-y-12">
                        <section>
                            <div className="flex items-center gap-4 mb-8">
                                <div className="p-3 rounded-2xl bg-neutral-900 border border-neutral-800 text-blue-500">
                                    <Shield size={24} />
                                </div>
                                <h2 className="text-2xl font-black uppercase tracking-tight">Готовые решения (Тарифы)</h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {tariffs.map(t => (
                                    <button
                                        key={t.id}
                                        onClick={() => selectTariff(t.id)}
                                        className={`text-left p-8 rounded-[2.5rem] border-2 transition-all relative overflow-hidden group ${selectedItems.tariffId === t.id ? 'bg-blue-600 border-blue-500 shadow-2xl shadow-blue-600/20 pt-12' : 'bg-neutral-900 border-neutral-800 hover:border-neutral-700'}`}
                                    >
                                        {selectedItems.tariffId === t.id && (
                                            <div className="absolute top-6 right-8 text-white">
                                                <Check size={24} strokeWidth={3} />
                                            </div>
                                        )}
                                        <h3 className={`text-xl font-black uppercase italic tracking-tighter mb-2 ${selectedItems.tariffId === t.id ? 'text-white' : 'text-neutral-200'}`}>{t.name}</h3>
                                        <p className={`text-xs mb-6 font-medium ${selectedItems.tariffId === t.id ? 'text-blue-100' : 'text-neutral-500'}`}>{t.description}</p>
                                        <div className="mt-auto">
                                            <p className={`text-[10px] font-black uppercase tracking-widest ${selectedItems.tariffId === t.id ? 'text-blue-200' : 'text-neutral-500'}`}>Стоимость от</p>
                                            <p className={`text-3xl font-black ${selectedItems.tariffId === t.id ? 'text-white' : 'text-blue-500'}`}>{t.price?.toLocaleString()} ₽</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </section>

                        <section>
                            <div className="flex items-center gap-4 mb-8">
                                <div className="p-3 rounded-2xl bg-neutral-900 border border-neutral-800 text-indigo-500">
                                    <Zap size={24} />
                                </div>
                                <h2 className="text-2xl font-black uppercase tracking-tight">Дополнительные услуги</h2>
                            </div>
                            <div className="space-y-4">
                                {services.map(s => (
                                    <button
                                        key={s.id}
                                        onClick={() => toggleService(s.id)}
                                        className={`w-full flex items-center justify-between p-6 rounded-3xl border transition-all ${selectedItems.services.includes(s.id) ? 'bg-indigo-600/10 border-indigo-500 shadow-lg shadow-indigo-600/5' : 'bg-neutral-900 border-neutral-800 hover:border-neutral-700'}`}
                                    >
                                        <div className="flex items-center gap-6">
                                            <div className={`p-4 rounded-2xl border transition-colors ${selectedItems.services.includes(s.id) ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-neutral-800 border-neutral-700 text-neutral-500'}`}>
                                                <Tag size={20} />
                                            </div>
                                            <div className="text-left">
                                                <h4 className="font-bold text-white uppercase text-sm tracking-tight">{s.name}</h4>
                                                <span className="text-[10px] text-neutral-500 font-black uppercase tracking-widest">{s.category || 'Услуга'}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-8">
                                            <div className="text-right">
                                                <p className="text-xl font-black text-white">{s.price?.toLocaleString()} ₽</p>
                                                <p className="text-[9px] text-neutral-500 font-black uppercase tracking-widest">за {s.unit}</p>
                                            </div>
                                            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${selectedItems.services.includes(s.id) ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-neutral-700 text-transparent'}`}>
                                                <Check size={16} strokeWidth={3} />
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </section>
                    </div>

                    {/* Summary sidebar */}
                    <div className="lg:sticky lg:top-12">
                        <div className="bg-neutral-900 border border-neutral-800 rounded-[3rem] p-10 overflow-hidden relative group">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-indigo-600" />
                            
                            <div className="flex items-center gap-3 mb-8">
                                <div className="p-2 rounded-xl bg-blue-600/10 text-blue-500">
                                    <Calculator size={20} />
                                </div>
                                <h3 className="text-lg font-black uppercase tracking-tight">Ваш расчет</h3>
                            </div>

                            <div className="mb-8 p-6 bg-neutral-900 border border-neutral-800 rounded-3xl">
                                <div className="flex justify-between items-center mb-4">
                                    <label className="text-[10px] font-black text-neutral-500 uppercase tracking-widest">Количество (шт.)</label>
                                    <span className="text-xl font-black text-white">{globalQuantity}</span>
                                </div>
                                <input 
                                    type="range" 
                                    min="1" max="100" 
                                    value={globalQuantity} 
                                    onChange={(e) => setGlobalQuantity(Number(e.target.value))}
                                    className="w-full h-2 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                />
                            </div>

                            <div className="space-y-6 mb-12">
                                {selectedItems.tariffId ? (
                                    <div className="flex justify-between items-start gap-4 animate-in slide-in-from-left-4">
                                        <div>
                                            <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-1">Тариф</p>
                                            <p className="font-bold text-white text-sm uppercase italic">{tariffs.find(t => t.id === selectedItems.tariffId)?.name}</p>
                                        </div>
                                        <p className="font-black text-white">{(tariffs.find(t => t.id === selectedItems.tariffId)?.price || 0).toLocaleString()} ₽</p>
                                    </div>
                                ) : null}

                                {selectedItems.services.map(sid => {
                                    const svc = services.find(s => s.id === sid);
                                    const itemPrice = svc?.unit === 'шт' ? (svc?.price || 0) * globalQuantity : (svc?.price || 0);
                                    return (
                                        <div key={sid} className="flex justify-between items-start gap-4 animate-in slide-in-from-left-4">
                                            <div>
                                                <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-1">Услуга {svc?.unit === 'шт' && `(${globalQuantity} шт)`}</p>
                                                <p className="font-bold text-neutral-300 text-sm">{svc?.name}</p>
                                            </div>
                                            <p className="font-black text-white">{itemPrice.toLocaleString()} ₽</p>
                                        </div>
                                    )
                                })}

                                {!selectedItems.tariffId && selectedItems.services.length === 0 && (
                                    <div className="py-10 text-center">
                                        <Package size={40} className="mx-auto text-neutral-800 mb-4" />
                                        <p className="text-xs text-neutral-500 font-bold uppercase tracking-widest">Выберите услуги <br />для расчета</p>
                                    </div>
                                )}
                            </div>

                            <div className="pt-8 border-t border-neutral-800">
                                <div className="flex justify-between items-center mb-10">
                                    <p className="text-[10px] font-black text-neutral-500 uppercase tracking-widest">Итоговая стоимость</p>
                                    <div className="text-right">
                                        <p className="text-4xl font-black tracking-tighter text-blue-500 leading-none">{calculateTotal().toLocaleString()} ₽</p>
                                        <p className="text-[9px] text-neutral-500 font-bold uppercase tracking-widest mt-1">Ориентировочно</p>
                                    </div>
                                </div>

                                <button 
                                    className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-neutral-800 disabled:text-neutral-500 p-6 rounded-3xl text-white font-black uppercase text-xs tracking-[0.2em] transition-all shadow-xl shadow-blue-600/20 active:scale-95 flex items-center justify-center gap-3"
                                    disabled={!selectedItems.tariffId && selectedItems.services.length === 0}
                                    onClick={() => alert('В разработке: Форма обратной связи')}
                                >
                                    Оставить заявку <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="mt-8 p-6 rounded-3xl bg-neutral-900/50 border border-neutral-800">
                            <div className="flex items-start gap-4">
                                <div className="mt-1 p-2 rounded-lg bg-emerald-600/10 text-emerald-500">
                                    <Info size={16} />
                                </div>
                                <p className="text-[10px] font-medium text-neutral-500 leading-relaxed uppercase">
                                    Все расчеты являются предварительными. Окончательная стоимость фиксируется в официальном договоре после обсуждения деталей проекта.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default PublicCalculator;
